export const getSafeProbability = (lastPrice: number, yesBid: number, yesAsk: number): number | undefined => {
    if (lastPrice < 10 && yesBid === 0) {
        return undefined;
    }
    if (lastPrice > 90 && yesAsk === 100) {
        return lastPrice;
    }

    const midSpread = Math.floor((yesBid + yesAsk) / 2);
    const spreadCents = Math.abs(yesBid - yesAsk);
    const SPREAD_LIMIT = 30;

    if (yesBid > 90 && yesAsk === 100) return midSpread;
    if (yesBid === 0 && yesAsk < 10) return undefined;
    if (lastPrice === 0) {
        if (spreadCents < SPREAD_LIMIT) {
            return midSpread;
        }
        return undefined;
    }

    return lastPrice;
};

/**
 * Scales non-negative probability weights to integer percentages that add up
 * to exactly 100. Remaining rounding points go to the largest fractional
 * remainders, with input order as the deterministic tie-breaker.
 *
 * Returns null when the weights cannot describe a distribution. Callers can
 * then keep their existing unpriced/empty-state behavior.
 */
export const normalizePercentagesToHundred = (weights: readonly number[]): number[] | null => {
    if (weights.length === 0 || weights.some((weight) => !Number.isFinite(weight) || weight < 0)) return null;

    const total = weights.reduce((sum, weight) => sum + weight, 0);
    if (total <= 0) return null;

    const exactPercentages = weights.map((weight) => (weight / total) * 100);
    const percentages = exactPercentages.map(Math.floor);
    const assigned = percentages.reduce((sum, percentage) => sum + percentage, 0);
    const remainderOrder = exactPercentages
        .map((percentage, index) => ({ fraction: percentage - percentages[index]!, index }))
        .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

    for (let index = 0; index < 100 - assigned; index += 1) {
        percentages[remainderOrder[index]!.index] += 1;
    }

    return percentages;
};

/**
 * Format a probability for display, matching BinaryMarket.lastTradedPriceString.
 *
 * Wraps getSafeProbability with the same formatting the event page "Chance"
 * column uses: values that round to 0 become "<1%", undefined becomes "--%",
 * except near-zero markets (lastPrice < 3¢, no bid) which still show "<1%".
 */
export const formatChancePercent = (lastPrice: number, yesBid: number, yesAsk: number): string => {
    const probability = getSafeProbability(lastPrice, yesBid, yesAsk);
    if (probability != null) {
        return probability < 1 ? '<1%' : `${probability}%`;
    }
    // getSafeProbability returns undefined for lastPrice < 10 && yesBid === 0,
    // but very low prices (< 3¢) are still near-zero odds, not missing data.
    return lastPrice > 0 && lastPrice < 3 && yesBid === 0 ? '<1%' : '--%';
};
